import React, { useState } from 'react';
import {
  Container,
  ListContainer,
  MenuContainer,
  MenuContainerWrapper,
  StyledTitle,
} from '@/components/MobileApproval/ApprovalWirte/styles';
import MobileBottomSheet from '@/components/@IntranetLibrary/MobileBottomSheet';
import { useGetApprovalType } from '@/hooks/apiHooks';
import { ApprovalType } from '@/types/api';
import SubHeaderContent from '@/components/MobileApproval/SubHeaderContent';
import CustomInput from '@/components/Organization/CustomInput';
import { FormProvider, useForm } from 'react-hook-form';
import { useDebounce } from 'usehooks-ts';
import NoData from '@/components/MobileApproval/NoData';
import CreateApproval from '@/components/MobileApproval/ApprovalWirte/CreateApproval';
import { useTranslation } from 'react-i18next';
import Image from 'next/image';
import FloatingButton from '@/components/FloatingButton';
import { useAtomValue } from 'jotai';
import { MyInfoState } from '@/recoil/atom';
import { useDialog } from '@/hooks/global-tools';
import { isAttendanceApprovalCompanyAllowed } from '@/components/Approval/CreateApprovalModal/attendanceApproval';
import WorkingHoursChangeModal from '@/components/MenuDrawer/MyMenuDrawer/SubMenu/WorkHours/WorkingHoursChangeModal';
import OvertimeRequestModal from '@/components/MenuDrawer/MyMenuDrawer/SubMenu/Overtime/OvertimeRequestModal';
import OvertimeAdjustModal from '@/components/MenuDrawer/MyMenuDrawer/SubMenu/Overtime/OvertimeAdjustModal';
import AnnualLeaveModal from '@/components/MenuDrawer/MyMenuDrawer/SubMenu/WorkHours/AnnualLeaveModal';

export const categorizeByTag = (data?: ApprovalType[]) => {
  const { t } = useTranslation();
  if (!data) return {};

  const categorizedData: { [key: string]: ApprovalType[] } = {};
  categorizedData[t('전체')] = [];

  data.forEach((item) => {
    const tag = item.tag;
    if (!categorizedData[tag]) {
      categorizedData[tag] = [];
    }
    categorizedData[t('전체')].push(item);
    categorizedData[tag].push(item);
  });

  // sortNumber 기준으로 정렬하는 함수
  const sortByPriority = (a: ApprovalType, b: ApprovalType) => {
    // sortNumber가 있는 것들을 우선 처리
    if (a.sortNumber !== null && b.sortNumber !== null) {
      return a.sortNumber - b.sortNumber;
    }
    if (a.sortNumber !== null && b.sortNumber === null) {
      return -1; // a가 우선
    }
    if (a.sortNumber === null && b.sortNumber !== null) {
      return 1; // b가 우선
    }
    // 둘 다 null인 경우 이름으로 정렬
    return a.name.localeCompare(b.name, 'ko-KR');
  };

  // '전체' 카테고리 정렬
  categorizedData[t('전체')] = categorizedData[t('전체')].sort(sortByPriority);

  // 카테고리 키를 정렬하고 새 객체에 복사
  const sortedCategorizedData: { [key: string]: ApprovalType[] } = {};
  sortedCategorizedData[t('전체')] = categorizedData[t('전체')]; // '전체'를 먼저 추가

  Object.keys(categorizedData)
    .filter((key) => key !== t('전체')) // '전체'를 제외하고 정렬
    .sort((a, b) => a.localeCompare(b, 'ko-KR'))
    .forEach((key) => {
      sortedCategorizedData[key] = categorizedData[key].sort(sortByPriority);
    });

  return sortedCategorizedData;
};

const ApprovalWirte = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);

  const toggleDrawer = (newOpen: boolean) => () => {
    setOpen(newOpen);
  };

  const dialog = useDialog();
  const myInfo = useAtomValue(MyInfoState);
  const { workingHours } = myInfo;

  // 데스크탑(CreateApprovalModal)과 동일하게 근태 양식은 양식별 Portal 모달로 연다.
  const [isWorkingHoursChangeOpen, setIsWorkingHoursChangeOpen] =
    useState(false);
  const [isOvertimeRequestOpen, setIsOvertimeRequestOpen] = useState(false);
  const [isOvertimeAdjustOpen, setIsOvertimeAdjustOpen] = useState(false);
  const [isAnnualLeaveOpen, setIsAnnualLeaveOpen] = useState(false);

  // 법인별 사용 제한 검사. 미허용 법인이면 알럿 노출 후 false 반환(진입 차단).
  const isAttendanceApprovalAllowed = (type: number) => {
    if (!isAttendanceApprovalCompanyAllowed(type, myInfo.companyCode)) {
      dialog.show({ message: t('해당 양식을 지원하지 않는 법인입니다.') });
      return false;
    }
    return true;
  };

  const handleOpenWorkingHoursChange = () => {
    if (!isAttendanceApprovalAllowed(222)) return;
    setOpen(false);
    setIsWorkingHoursChangeOpen(true);
  };

  const handleOpenOvertimeRequest = () => {
    if (!isAttendanceApprovalAllowed(223)) return;
    setOpen(false);
    setIsOvertimeRequestOpen(true);
  };

  const handleOpenOvertimeAdjust = () => {
    if (!isAttendanceApprovalAllowed(224)) return;
    setOpen(false);
    setIsOvertimeAdjustOpen(true);
  };

  const handleOpenAnnualLeave = () => {
    if (!isAttendanceApprovalAllowed(231)) return;
    setOpen(false);
    setIsAnnualLeaveOpen(true);
  };

  const { data } = useGetApprovalType();

  const categorizeByTagData = categorizeByTag(data);

  const resultData = Object.entries(categorizeByTagData).map(
    ([key, value]) => ({
      menu: key,
      list: value,
    }),
  );

  const methods = useForm<{ keyword: string }>({
    defaultValues: { keyword: '' },
  });
  const searchString = methods.watch('keyword');
  const debouncedValue = useDebounce(searchString, 500);

  const [selectedId, setSelectedId] = useState(0);
  const handleClick = (id: number) => setSelectedId(id);

  // 근태 양식별 Portal 모달 핸들러 (CreateApproval 로 전달)
  const attendanceModalHandlers = {
    onOpenWorkingHoursChange: handleOpenWorkingHoursChange,
    onOpenOvertimeRequest: handleOpenOvertimeRequest,
    onOpenOvertimeAdjust: handleOpenOvertimeAdjust,
    onOpenAnnualLeave: handleOpenAnnualLeave,
  };

  const searchList =
    resultData[selectedId]?.list
      .map(
        (res, idx) =>
          res.name.includes(debouncedValue) && (
            <CreateApproval
              key={`newApproval-${idx}`}
              name={res.name}
              type={res.type}
              onClose={toggleDrawer(false)}
              {...attendanceModalHandlers}
            />
          ),
      )
      .filter((element) => element !== false) || [];

  return (
    <>
      <FloatingButton
        show={true}
        handleClick={handleOpen}
        style={{ backgroundColor: 'black' }}
      >
        <Image
          src={'/icons/mobileEdit.svg'}
          alt={'edit'}
          width={24}
          height={24}
        />
      </FloatingButton>
      <MobileBottomSheet
        open={open}
        onClose={toggleDrawer(false)}
        height="auto"
      >
        <Container>
          <StyledTitle>{t('양식 선택')}</StyledTitle>
          <MenuContainerWrapper>
            <MenuContainer>
              {resultData.map(({ menu, list }, idx) => (
                <SubHeaderContent
                  key={`menuList-${menu}`}
                  id={idx}
                  title={menu}
                  clicked={selectedId === idx}
                  handleClick={handleClick}
                />
              ))}
            </MenuContainer>
          </MenuContainerWrapper>
          <FormProvider {...methods}>
            <CustomInput placeHolder={t('양식명을 검색해 주세요.')} />
          </FormProvider>
          <ListContainer>
            {debouncedValue ? (
              searchList.length > 0 ? (
                searchList
              ) : (
                <NoData
                  title={
                    <div>
                      {t('검색어와 일치하는 문서가 없어요.')}
                      <br />
                      {t('검색어를 다시 한번 확인해 주세요.')}
                    </div>
                  }
                />
              )
            ) : (
              resultData[selectedId] &&
              resultData[selectedId].list.map((res, idx) => (
                <CreateApproval
                  key={`newApproval-${idx}`}
                  name={res.name}
                  type={res.type}
                  onClose={toggleDrawer(false)}
                  {...attendanceModalHandlers}
                />
              ))
            )}
          </ListContainer>
        </Container>
      </MobileBottomSheet>

      <WorkingHoursChangeModal
        open={isWorkingHoursChangeOpen}
        onClose={() => setIsWorkingHoursChangeOpen(false)}
        myInfo={myInfo}
        menuOnClose={() => setIsWorkingHoursChangeOpen(false)}
      />
      <OvertimeRequestModal
        open={isOvertimeRequestOpen}
        onClose={() => setIsOvertimeRequestOpen(false)}
        workingHours={workingHours ?? undefined}
      />
      <OvertimeAdjustModal
        open={isOvertimeAdjustOpen}
        onClose={() => setIsOvertimeAdjustOpen(false)}
        workingHours={workingHours ?? undefined}
      />
      <AnnualLeaveModal
        open={isAnnualLeaveOpen}
        onClose={() => setIsAnnualLeaveOpen(false)}
      />
    </>
  );
};
export default ApprovalWirte;
